import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import {
  CreateContentComponentTagBodyParams,
  UpdateContentComponentTagBodyParams,
  createContentComponentTag,
  getContentComponentTagBySlug,
  updateContentComponentTag,
  updateContentComponentTagStatus,
} from '~/services/ContentService';

export const CONTENT_TAG_STATUS = {
  DRAFT: 'draft',
  PUBLISHED: 'published',
  CHANGED: 'changed',
  ARCHIVED: 'archived',
} as const;

export type ContentTagStatus = (typeof CONTENT_TAG_STATUS)[keyof typeof CONTENT_TAG_STATUS];

export const CONTENT_TAG_STATUS_LABELS: Record<ContentTagStatus, string> = {
  [CONTENT_TAG_STATUS.DRAFT]: 'Draft',
  [CONTENT_TAG_STATUS.PUBLISHED]: 'Published',
  [CONTENT_TAG_STATUS.CHANGED]: 'Changed',
  [CONTENT_TAG_STATUS.ARCHIVED]: 'Archived',
};

export const CONTENT_TAG_STATUS_COLORS: Record<ContentTagStatus, string> = {
  [CONTENT_TAG_STATUS.DRAFT]: '#9e9e9e',
  [CONTENT_TAG_STATUS.PUBLISHED]: '#4caf50',
  [CONTENT_TAG_STATUS.CHANGED]: '#ff9800',
  [CONTENT_TAG_STATUS.ARCHIVED]: '#795548',
};

export const CONTENT_TAG_SELECTABLE_STATUSES = [CONTENT_TAG_STATUS.DRAFT, CONTENT_TAG_STATUS.PUBLISHED] as const;

export type ContentComponentTagFields = {
  id?: string;
  contentTypeId?: string;
  createdAt?: string;
  updatedAt?: string;
  slug?: string;
  brand?: {
    name: string;
    contentTypeId?: string;
  };
  icon?: string;
  title?: string;
  subtitle?: string;
  linkTo?: string;
  linkText?: string;
  status?: ContentTagStatus;
};

export type ContentBrand = {
  id: string;
  name: string;
  contentTypeId?: string;
};

export function useCreateContentComponentTags() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async (paramsList: Array<CreateContentComponentTagBodyParams>) => {
      const createPromises = paramsList.map((params) => createContentComponentTag(params));
      const createdIds = await Promise.all(createPromises);

      const publishPromises = createdIds.map((id) => updateContentComponentTagStatus(id, CONTENT_TAG_STATUS.PUBLISHED));
      await Promise.all(publishPromises);

      return createdIds;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['content', 'componentTag'] });
      enqueueSnackbar('Content tags created successfully', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Error creating content tags: ${error.message}`, { variant: 'error' });
    },
  });
}

export type UpdateComponentTagValuesParams = UpdateContentComponentTagBodyParams & { id: string };

export type UpdateComponentTagStatusParams = {
  id: string;
  status: ContentTagStatus;
};

export type UpdateContentComponentTagsParams = {
  values: Array<UpdateComponentTagValuesParams>;
  statuses: Array<UpdateComponentTagStatusParams>;
};

export function useUpdateContentComponentTags() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async (params: UpdateContentComponentTagsParams) => {
      const { values, statuses } = params;

      if (values.length > 0) {
        const valuePromises = values.map((value) => {
          const { id, ...valueParams } = value;
          return updateContentComponentTag(id, valueParams);
        });
        await Promise.all(valuePromises);
      }

      if (statuses.length > 0) {
        const statusPromises = statuses.map((status) => {
          const { id, status: statusParam } = status;
          return updateContentComponentTagStatus(id, statusParam);
        });
        await Promise.all(statusPromises);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['content', 'componentTag'], refetchType: 'all' });
      enqueueSnackbar('Content tags updated successfully', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Error updating content tags: ${error.message}`, { variant: 'error' });
    },
  });
}

type ContentComponentTagsSlugs = {
  offerBanner: string;
  searchBanner: string;
  offerPill: string;
  inflowPill: string;
  searchPill: string;
};

export type ContentComponentTagsResult = {
  offerBanner: ContentComponentTagFields | null;
  searchBanner: ContentComponentTagFields | null;
  offerPill: ContentComponentTagFields | null;
  inflowPill: ContentComponentTagFields | null;
  searchPill: ContentComponentTagFields | null;
  pill: ContentComponentTagFields | null;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  errors: {
    offerBanner: Error | null;
    searchBanner: Error | null;
    pill: Error | null;
  };
};

export function useContentComponentTags(
  slugs: ContentComponentTagsSlugs,
  options: { enabled?: boolean } = {},
): ContentComponentTagsResult {
  const { enabled = true } = options;

  const queries = useQueries({
    queries: [
      {
        queryKey: ['content', 'componentTag', 'slug', slugs.offerBanner],
        queryFn: () => getContentComponentTagBySlug(slugs.offerBanner),
        enabled: enabled && !!slugs.offerBanner,
        retry: false,
        staleTime: 1 * 60 * 1000,
        refetchOnWindowFocus: false,
      },
      {
        queryKey: ['content', 'componentTag', 'slug', slugs.searchBanner],
        queryFn: () => getContentComponentTagBySlug(slugs.searchBanner),
        enabled: enabled && !!slugs.searchBanner,
        retry: false,
        staleTime: 1 * 60 * 1000,
        refetchOnWindowFocus: false,
      },
      {
        queryKey: ['content', 'componentTag', 'slug', slugs.offerPill],
        queryFn: () => getContentComponentTagBySlug(slugs.offerPill),
        enabled: enabled && !!slugs.offerPill,
        retry: false,
        staleTime: 1 * 60 * 1000,
        refetchOnWindowFocus: false,
      },
      {
        queryKey: ['content', 'componentTag', 'slug', slugs.inflowPill],
        queryFn: () => getContentComponentTagBySlug(slugs.inflowPill),
        enabled: enabled && !!slugs.inflowPill,
        retry: false,
        staleTime: 1 * 60 * 1000,
        refetchOnWindowFocus: false,
      },
      {
        queryKey: ['content', 'componentTag', 'slug', slugs.searchPill],
        queryFn: () => getContentComponentTagBySlug(slugs.searchPill),
        enabled: enabled && !!slugs.searchPill,
        retry: false,
        staleTime: 1 * 60 * 1000,
        refetchOnWindowFocus: false,
      },
    ],
  });

  const [offerBannerQuery, searchBannerQuery, offerPillQuery, inflowPillQuery, searchPillQuery] = queries;

  const offerPill = offerPillQuery.data ?? null;
  const inflowPill = inflowPillQuery.data ?? null;
  const searchPill = searchPillQuery.data ?? null;

  return {
    offerBanner: offerBannerQuery.data ?? null,
    searchBanner: searchBannerQuery.data ?? null,
    offerPill,
    inflowPill,
    searchPill,
    pill: offerPill,
    isLoading:
      offerBannerQuery.isLoading ||
      searchBannerQuery.isLoading ||
      offerPillQuery.isLoading ||
      inflowPillQuery.isLoading ||
      searchPillQuery.isLoading,
    isFetching:
      offerBannerQuery.isFetching ||
      searchBannerQuery.isFetching ||
      offerPillQuery.isFetching ||
      inflowPillQuery.isFetching ||
      searchPillQuery.isFetching,
    isError:
      offerBannerQuery.isError ||
      searchBannerQuery.isError ||
      offerPillQuery.isError ||
      inflowPillQuery.isError ||
      searchPillQuery.isError,
    errors: {
      offerBanner: offerBannerQuery.error ?? null,
      searchBanner: searchBannerQuery.error ?? null,
      pill: offerPillQuery.error ?? inflowPillQuery.error ?? searchPillQuery.error ?? null,
    },
  };
}
